import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Download, Globe, BookOpen, Wrench, Rocket, ClipboardCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getToolkitByCountryId, getAllToolkitCountries } from '../data/toolkits';
import OverviewSection from './toolkit/OverviewSection';

var API_URL = process.env.REACT_APP_BACKEND_URL;
import StorytellingSection from './toolkit/StorytellingSection';
import ToolsSection from './toolkit/ToolsSection';
import ActivationSection from './toolkit/ActivationSection';
import MeasurementSection from './toolkit/MeasurementSection';

var CountryToolkitPage = function() {
  var params = useParams();
  var countryId = params.countryId;
  var navigate = useNavigate();
  var [activeTab, setActiveTab] = useState('overview');
  var [downloading, setDownloading] = useState(false);
  var auth = useAuth();

  var toolkit = getToolkitByCountryId(countryId);
  var allCountries = getAllToolkitCountries();

  function handleDownload() {
    setDownloading(true);
    // Direct browser download via native link — most reliable across all browsers
    var url = API_URL + '/api/toolkit/' + countryId + '/download';
    var link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'GrowYourTomorrow_' + countryId.replace(/-/g, '_') + '_Toolkit.pdf');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(function() {
      document.body.removeChild(link);
      setDownloading(false);
    }, 2000);
  }

  if (!toolkit) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Select a Country Toolkit</h2>
            <p className="text-gray-600 mb-8">Choose a country below to view its toolkit</p>
            <div className="flex flex-wrap gap-3 justify-center">
              {allCountries.map(function(c) {
                return (
                  <Button
                    key={c.id}
                    onClick={function() { navigate('/admin/toolkits/' + c.id); }}
                    variant="outline"
                    className="border-[#07743F] text-[#07743F] hover:bg-green-50"
                    data-testid={'toolkit-select-' + c.id}
                  >
                    <span className="mr-2">{c.flag}</span>
                    {c.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white" data-testid="toolkit-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-700 to-green-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
            <div className="text-8xl">{toolkit.flag}</div>
            <div className="text-center md:text-left">
              <div className="px-4 py-1 bg-white/20 rounded-full text-sm inline-block mb-4">{toolkit.region}</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{toolkit.name} Toolkit</h1>
              <p className="text-green-100 text-lg">Complete storytelling guide for YEFFA country teams</p>
            </div>
          </div>
          <div className="mt-10 text-center md:text-left max-w-4xl mx-auto">
            <Button onClick={handleDownload} disabled={downloading} className="bg-white text-green-800 hover:bg-green-50 px-8 py-6 text-lg" data-testid="toolkit-download-btn">
              {downloading ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Download className="w-5 h-5 mr-3" />}
              {downloading ? 'Generating PDF...' : 'Download Toolkit (PDF)'}
            </Button>
          </div>
        </div>
      </section>

      {/* Country Navigation - links to admin paths */}
      <section className="bg-white border-b border-green-200 py-4 overflow-x-auto">
        <div className="container mx-auto px-4">
          <div className="flex gap-2 min-w-max">
            {allCountries.map(function(c) {
              var isActive = c.id === countryId;
              return (
                <button
                  key={c.id}
                  onClick={function() { navigate('/admin/toolkits/' + c.id); }}
                  className={'px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ' + (isActive ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-green-100')}
                  data-testid={'toolkit-nav-' + c.id}
                >
                  <span>{c.flag}</span>
                  <span className="hidden sm:inline">{c.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tabs Content */}
      <div className="container mx-auto px-4 py-10">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="grid w-full grid-cols-5 bg-white border border-green-200 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                <Globe className="w-4 h-4 mr-2" />Overview
              </TabsTrigger>
              <TabsTrigger value="storytelling" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                <BookOpen className="w-4 h-4 mr-2" />Story
              </TabsTrigger>
              <TabsTrigger value="tools" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                <Wrench className="w-4 h-4 mr-2" />Tools
              </TabsTrigger>
              <TabsTrigger value="activation" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                <Rocket className="w-4 h-4 mr-2" />Activate
              </TabsTrigger>
              <TabsTrigger value="measurement" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                <ClipboardCheck className="w-4 h-4 mr-2" />M&E
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewSection toolkit={toolkit} />
            </TabsContent>
            <TabsContent value="storytelling">
              <StorytellingSection toolkit={toolkit} />
            </TabsContent>
            <TabsContent value="tools">
              <ToolsSection toolkit={toolkit} />
            </TabsContent>
            <TabsContent value="activation">
              <ActivationSection toolkit={toolkit} />
            </TabsContent>
            <TabsContent value="measurement">
              <MeasurementSection toolkit={toolkit} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CountryToolkitPage;
